import { gql, useQuery } from "@apollo/client";
import * as ChannelService from "@channel.io/channel-web-sdk-loader";
import classNames from "classnames";
import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Outlet, useLocation } from "react-router-dom";

import type { IMediaMetaObjectQuery } from "@repo/shopify/queries/metaobject-gql";
import { mediaMetaobjectQuery } from "@repo/shopify/queries/metaobject-gql";

import Button from "@atoms/Button";
import Link from "@atoms/Link";
import Toast from "@atoms/Toast";

import Modal from "@molecules/Modal";
import NewsLetterInput from "@molecules/NewsLetterInput";
import Popup from "@molecules/Popup";
import SubModal from "@molecules/SubModal";

import ChannelTalk from "@organisms/ChannelTalk";
import Footer from "@organisms/Footer";
import Header from "@organisms/Header";

import { DEFAULT_DESCRIPTION, DEFAULT_TITLE, META_TAGS } from "@const/meta";
import { URLS } from "@const/urls";

import useCheckLocation from "@hooks/useCheckLocation";
import useLanguage from "@hooks/useLanguage";

import { useLayoutStore } from "@store/layout";

ChannelService.loadScript();

export default function PageLayout() {
  const { pathname, hash } = useLocation();

  const { openSubModal, closeSubModal, toast, modal, isShowFooter, meta, openPopup, popup } = useLayoutStore();
  const { lang } = useLanguage();

  const { isCheckout, isWeChat, isHome, isDanielJohnston, isProduct } = useCheckLocation();

  const [showSubModal, setShowSubModal] = useState<boolean>(false);

  useEffect(() => {
    setTimeout(() => {
      setShowSubModal(true);
    }, 2000);
  }, []);

  const matchedUrlMetaTag = useMemo(() => META_TAGS[pathname as keyof typeof META_TAGS], [pathname]);

  const { data: notice, loading: noticeLoading } = useQuery<IMediaMetaObjectQuery>(gql(mediaMetaobjectQuery.query), {
    variables: { type: "notice", first: 2, sortKey: "updated_at" },
    onError: (error) => {
      throw new Error(error.message);
    },
  });

  const { data: _popup, loading: popupLoading } = useQuery<IMediaMetaObjectQuery>(gql(mediaMetaobjectQuery.query), {
    variables: { type: "popup", first: 6, reverse: true },
    onError: (error) => {
      throw new Error(error.message);
    },
  });

  const noticeList = useMemo(
    () =>
      notice?.metaobjects?.edges.map(({ node }) => {
        const title = node?.fields.find((item) => item.key === "title");
        const content = node?.fields.find((item) => item.key === "content");
        return { handle: node.handle, title: title?.value || "", content: content?.value || "" };
      }) || [],
    [notice],
  );

  useEffect(() => {
    if (isCheckout) {
      ChannelService.shutdown();
    }
  }, [isCheckout]);

  useEffect(() => {
    const cookie = localStorage.getItem("cookie");

    const modalList = [];

    noticeList.forEach((item) => {
      const noticeLocal = localStorage.getItem(item?.handle || "");
      if (noticeLocal) {
        // 24시간이 지난 경우
        const expireTime = JSON.parse(noticeLocal) as number;
        if (new Date().getTime() > (expireTime || 0)) {
          modalList.push({
            title: item?.title || "",
            content: item?.content,
            id: "notice",
            handle: item?.handle || "",
          });
        }
      } else {
        // 새로운 공지 일 경우
        modalList.push({
          title: item?.title || "",
          content: item?.content,
          id: "notice",
          handle: item?.handle || "",
        });
      }
    });

    if (lang === "en" && !cookie) {
      modalList.push({
        title: "Cookies Management",
        content: (
          <div className="flex flex-col gap-2.5">
            <p>
              This website uses cookie to ensure you get the best experience on our website.{" "}
              <Link as="text" href={URLS.PRIVACY_POLICY}>
                Privacy Policy
              </Link>
            </p>
            <Button
              as="square"
              onClick={() => {
                localStorage.setItem("cookie", "true");
                closeSubModal();
              }}
            >
              Agree & Close
            </Button>
          </div>
        ),
        id: "cookie",
      });
    }

    openSubModal(modalList);
  }, [noticeLoading, lang]);

  useEffect(() => {
    const popupClose = localStorage.getItem("popup");

    if (popupClose || !_popup?.metaobjects.edges.length) return;

    const popupData = _popup?.metaobjects?.edges.filter(({ node }) => !node.handle.includes("test"));
    if (!popupData.length) return;

    const title = popupData?.[0]?.node?.fields.find((item) => item.key === "title")?.value || "";
    const description = popupData?.[0]?.node?.fields.find((item) => item.key === "description")?.value || "";
    const buttonText = popupData?.[0]?.node?.fields.find((item) => item.key === "button_text")?.value || "";
    const url = popupData?.[0]?.node?.fields.find((item) => item.key === "url")?.value || "";
    const imageReference = popupData?.[0]?.node?.fields.find((item) => item.key === "image")?.reference;
    const image = imageReference?.__typename === "MediaImage" ? imageReference?.image : undefined;

    const startDate = popupData?.[0]?.node?.fields.find((item) => item.key === "start_date")?.value || "";
    const endDate = popupData?.[0]?.node?.fields.find((item) => item.key === "end_date")?.value || "";

    openPopup({
      title,
      description,
      id: "popup",
      image,
      buttonText,
      url,
      startDate: startDate ? new Date(startDate) : new Date(),
      endDate: endDate ? new Date(endDate) : new Date("9999-12-31"),
    });
  }, [_popup]);

  useEffect(() => {
    if (hash.includes("newsletter")) {
      setTimeout(() => {
        openSubModal([{ title: "Subscribe to our Newsletter", content: <NewsLetterInput />, id: "newsletter" }]);
      }, 300);
    }
  }, [hash]);

  return (
    <>
      <Helmet defaultTitle={DEFAULT_TITLE}>
        <title>{matchedUrlMetaTag?.title ?? meta.title}</title>
        <meta content={matchedUrlMetaTag?.description ?? meta.description ?? DEFAULT_DESCRIPTION} name="description" />
        <meta content={matchedUrlMetaTag?.title ?? meta.title ?? DEFAULT_TITLE} property="og:title" />
        <meta content={matchedUrlMetaTag?.description ?? meta.description ?? DEFAULT_DESCRIPTION} property="og:description" />
        <meta content={matchedUrlMetaTag?.title ?? meta.title ?? DEFAULT_TITLE} property="twitter:title" />
        <meta content={matchedUrlMetaTag?.description ?? meta.description ?? DEFAULT_DESCRIPTION} property="twitter:description" />
      </Helmet>
      {!isCheckout && !isWeChat && <Header />}
      <main
        className={classNames(
          "tnt-gap-x pc:grid-cols-12 pc:px-5 relative grid min-h-dvh auto-rows-min grid-cols-8 px-4 dark:text-white",
          isDanielJohnston ? "dark:bg-dj-black" : "dark:bg-black",
          !isHome && "gap-y-2.5",
          isProduct ? "pc:pt-12.5 pt-4" : [!isCheckout && !isHome && "pc:pt-17.5 pt-13.5"],
        )}
      >
        <Outlet />
        {!isCheckout && !isWeChat && lang !== "jp" ? (
          <Button className="pc:right-5 pc:bottom-5 fixed right-4 bottom-4 z-10" id="channelTalk">
            <ChannelTalk />
          </Button>
        ) : null}
      </main>
      {isShowFooter && !isCheckout && !isWeChat ? <Footer /> : null}
      {modal ? <Modal /> : null}
      {toast.length > 1 ? <Toast /> : null}
      {showSubModal ? <SubModal /> : null}
      {popup ? <Popup /> : null}
    </>
  );
}
